import { useNavigation } from '@react-navigation/native';
import { mapStyles } from '@styles/StyleSheetStyles';
import { IconMap, Meta, theme, ThreeDotLoader } from 'aimo-ui';
import useBackButton from 'hooks/useBackButton';
import useBottomMenuHeight from 'hooks/useBottomMenuHeight';
import useIsDesktop from 'hooks/useIsDesktop';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Shadow } from 'react-native-shadow-2';
import { useMapStore } from 'stores/mapStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import styled from 'styled-components/native';
import { RootNavProps } from 'types/navigation';
import { isWeb } from 'utils/deviceUtils';
import { checkLocationPermissions } from 'utils/mapUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import Icon from '../icons/Icon';
import { useUiStore } from 'stores/uiStore';

const AbsoluteView = styled.View<{
  top: number;
  left?: boolean;
  forceTop?: boolean;
}>`
  position: absolute;
  ${({ top, forceTop }) =>
    isWeb && !forceTop ? 'bottom: 120px' : 'top: ' + top + 'px'};
  ${({ left }) => (left ? 'left: 20px' : 'right: 7px')};
`;

const LogoView = styled.View<{ top: number }>`
  z-index: ${isWeb ? 1 : 0};
  position: absolute;
  top: ${(p) => p.top}px;
  margin-top: -5px;
  left: 24px;
`;

const MapButton = styled.TouchableOpacity`
  background: ${theme.colors.white};
  padding: 12px;
  border-radius: 60px;
  max-width: 48px;
  max-height: 48px;
  box-shadow: 0px 8px 16px rgba(1, 1, 1, 0.3);
`;

export const UserLocationAndFilterButton = ({
  testID = 'find-location-button',
}: {
  testID?: string;
}) => {
  const { top } = useSafeAreaInsets();
  const currentLocation = useMapStore((state) => state.userLocation);
  const filters = useMapStore((state) => state.mapFilters);
  const activeFilterCount = Object.values(filters).filter(
    (value) => value === true
  ).length;
  const { t } = useTranslation();

  const tabHeight = useBottomMenuHeight();
  const isDesktop = useIsDesktop();
  const handleNavigateToUserLocation = async () => {
    if (currentLocation) {
      useMapStore.getState().setMapCoordinates({
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
      });
    }
    checkLocationPermissions();
  };
  const handleFilterOpen = async () => {
    useUiStore.getState().setSheetModal('zoneFilter');
  };

  const topMargin = () => {
    if (!isDesktop && isWeb) {
      return tabHeight + 100;
    }
    if (top < 15) {
      return 15;
    }
    return top;
  };

  return (
    <AbsoluteView
      forceTop={!isDesktop && isWeb}
      top={topMargin()}
      {...testIdentifiers((testID as string) && `${testID}-container`)}>
      <MapButton
        onPress={handleNavigateToUserLocation}
        {...testIdentifiers(testID as string)}
        style={mapStyles().mapButton}
        activeOpacity={0.8}
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel={t(
          'ariaLabels.map.currentLocation',
          'Show current location'
        )}
        accessibilityHint={t(
          'ariaLabels.map.currentLocationHint',
          'Centers the map on your current location'
        )}>
        <Icon
          name="Location-Filled-01"
          color={currentLocation ? theme.colors.black : theme.colors.boost.red}
        />
      </MapButton>
      <View style={{ height: 10 }} />
      <View style={{ position: 'relative' }}>
        <MapButton
          onPress={handleFilterOpen}
          {...testIdentifiers('find-filter-button')}
          style={mapStyles().mapButton}
          activeOpacity={0.8}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={
            activeFilterCount > 0
              ? t('ariaLabels.map.filters.active') + activeFilterCount
              : t('ariaLabels.map.filters.button', 'Filter locations')
          }
          accessibilityHint={t(
            'ariaLabels.map.filters.hint',
            'Opens filter options'
          )}>
          <Icon
            name="Filter"
            color={
              activeFilterCount > 0
                ? theme.colors.boost.green
                : theme.colors.black
            }
          />
        </MapButton>
        {activeFilterCount > 0 && (
          <View style={styles.filterNumber} importantForAccessibility="no">
            <Meta
              {...testIdentifiers('active-filter-count')}
              testID="active-filter-count"
              color={theme.colors.white}
              size="xs"
              weight="bold">
              {activeFilterCount}
            </Meta>
          </View>
        )}
      </View>
    </AbsoluteView>
  );
};

export const BackButton = ({ testID }: { testID?: string }) => {
  const { top } = useSafeAreaInsets();
  const { t } = useTranslation();

  const { navigate, goBack, canGoBack } = useNavigation<RootNavProps>();
  const handleNavigateBack = () => {
    if (canGoBack()) {
      useParkzoneStore.getState().setSelectedParkingSpace(undefined);
      goBack();
    } else {
      navigate('mainTabs', { screen: 'parkScreen' });
    }
    return true;
  };

  useBackButton(handleNavigateBack);

  return (
    <AbsoluteView
      top={top < 15 ? 15 : top}
      forceTop
      left
      testID={testID && `${testID}-container`}>
      <MapButton
        onPress={handleNavigateBack}
        {...testIdentifiers(testID as string)}
        style={mapStyles().mapButton}
        activeOpacity={0.8}
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel={t('ariaLabels.goBack', 'Go back')}
        accessibilityHint={t(
          'ariaLabels.goBackHint',
          'Returns to the previous screen'
        )}>
        <Icon name="back" size="m" color={theme.colors.black} />
      </MapButton>
    </AbsoluteView>
  );
};

export const Logo = ({ testID }: { testID?: string }) => {
  const { top } = useSafeAreaInsets();
  const { t } = useTranslation();
  const topNumber = () => {
    if (isWeb) {
      return 21;
    }
    return top < 15 ? 15 : top;
  };
  const LogoComponent = IconMap?.Logo_black;

  if (!LogoComponent) {
    return <></>;
  }
  return (
    <LogoView
      top={topNumber()}
      accessible={true}
      accessibilityRole="image"
      accessibilityLabel={t('ariaLabels.aimoLogo', 'Aimo logo')}
      {...testIdentifiers((testID as string) && `${testID}-container`)}>
      <LogoComponent
        width="55"
        height="55"
        {...testIdentifiers(testID as string)}
      />
    </LogoView>
  );
};

const Indicator = ({ children }: { children: React.ReactNode }) => {
  const { top } = useSafeAreaInsets();

  return (
    <Shadow
      containerStyle={{ ...styles.shadowContainer, top }}
      style={styles.container}>
      {children}
    </Shadow>
  );
};

export const MapInfoIndicators = ({
  showZoomedIndicator,
  zonesLoading,
}: {
  showZoomedIndicator: boolean;
  zonesLoading: boolean;
}) => {
  const { t } = useTranslation();

  if (zonesLoading) {
    return (
      <Indicator>
        <ThreeDotLoader />
      </Indicator>
    );
  }
  if (showZoomedIndicator) {
    return (
      <Indicator>
        <Meta numberOfLines={2} color={theme.colors.gray300} center>
          {t('parking.streetParkingNotification')}
        </Meta>
      </Indicator>
    );
  }
  return null;
};

const styles = StyleSheet.create({
  shadowContainer: {
    position: 'absolute',
    flex: 1,
    flexWrap: 'wrap',
    alignSelf: 'center',
    marginHorizontal: '25%',
    marginTop: 10,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors.white,
    borderRadius: 60,
    padding: 7,
  },
  filterNumber: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: theme.colors.boost.green,
    borderRadius: 10,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
